import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, useAnimation } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Wrench, CheckCircle, Settings, Tool, Zap } from 'lucide-react';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import pumps from '../../assets/Images/services/pumps.webp'

// Animation variants
const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const item = {
  hidden: { opacity: 0, y: 20 },
  show: { 
    opacity: 1, 
    y: 0,
    transition: {
      duration: 0.5
    }
  }
};

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: [0.16, 1, 0.3, 1]
    }
  }
};

const fadeIn = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      duration: 0.6,
      ease: 'easeOut'
    }
  }
};

const HydraulicPumpReconditioning = () => {
  const navigate = useNavigate();

  const keyPoints = [
    'Specialized in repairing piston, gear, and vane-type hydraulic pumps',
    'All pumps tested for flow rate, pressure, and noise level',
    'Replacement of worn shafts, bearings, and seals',
    'Dynamic test bench calibration after rebuild',
    'Cost-effective alternative to full pump replacement'
  ];

  const processSteps = [
    {
      title: 'Initial Assessment',
      description: 'Pump performance and wear patterns are analyzed in detail.'
    },
    {
      title: 'Disassembly & Cleaning',
      description: 'Internal parts are carefully dismantled and cleaned ultrasonically.'
    },
    {
      title: 'Component Replacement',
      description: 'Worn rotors, shafts, and bearings are replaced with OEM-grade components.'
    },
    {
      title: 'Reassembly',
      description: 'Pump is rebuilt with correct torque and alignment specifications.'
    },
    {
      title: 'Testing & Calibration',
      description: 'Tested for flow, pressure, and noise using calibrated test benches.'
    },
    {
      title: 'Performance Validation',
      description: 'Results compared with original manufacturer benchmarks.'
    }
  ];

  const objectives = [
    'Restore hydraulic pump efficiency to 100% operational standard',
    'Prevent failures caused by contamination or cavitation',
    'Reduce downtime through faster, reliable reconditioning',
    'Ensure consistent flow and pressure control',
    'Deliver guaranteed performance with full test reports'
  ];

  const controls = useAnimation();
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true
  });

  useEffect(() => {
    if (inView) {
      controls.start('show');
    }
  }, [controls, inView]);

  return (
    <div className="text-gray-800 leading-relaxed max-w-full overflow-x-hidden pt-20 md:pt-24">
      <Header />
      
      {/* Hero Section */}
      <motion.section 
        ref={ref}
        initial={{ opacity: 0, y: 20 }}
        animate={controls}
        variants={fadeInUp}
        className="relative bg-gray-50 py-16 md:py-24 overflow-hidden"
      >
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center">
            {/* Left side - Text content */}
            <div className="md:w-1/2 mb-10 md:mb-0 md:pr-10">
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#00325b] mb-4 sm:mb-6">Hydraulic Pump Reconditioning</h1>
              <p className="text-lg sm:text-xl text-gray-700 mb-6 sm:mb-8">
                Expert reconditioning and repair services for all types of hydraulic pumps, restoring them to like-new performance and efficiency.
              </p>
              <button 
                onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
                className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-8 rounded-lg transition-colors duration-300"
              >
                Get a Free Quote
              </button>
            </div>
            
            {/* Right side - Image */}
            <div className="md:w-1/2 relative">
              <div className="relative rounded-xl overflow-hidden shadow-2xl">
                <img 
                  src={pumps} 
                  alt="Hydraulic Pump Reconditioning"
                  className="w-full h-auto rounded-xl transform hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-blue-600/20 to-transparent rounded-xl"></div>
              </div>
              <div className="hidden md:block absolute -bottom-6 -left-6 w-32 h-32 bg-blue-100 rounded-full -z-10"></div>
              <div className="hidden md:block absolute -top-6 -right-6 w-24 h-24 bg-yellow-100 rounded-full -z-10"></div>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Description Section */}
      <motion.section 
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1, transition: { duration: 0.6 } }}
        viewport={{ once: true }}
        className="relative py-20 bg-gradient-to-br from-blue-50 to-gray-50 overflow-hidden"
      >
        <div className="absolute top-0 left-0 w-full h-full opacity-10">
          <div className="absolute top-20 -left-20 w-64 h-64 bg-blue-400 rounded-full mix-blend-multiply filter blur-3xl"></div>
          <div className="absolute bottom-20 -right-20 w-64 h-64 bg-yellow-400 rounded-full mix-blend-multiply filter blur-3xl"></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-5xl mx-auto text-center mb-16">
            <span className="inline-block px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-medium mb-4">
              Our Expertise
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Comprehensive Hydraulic <span className="text-blue-600">Pump Solutions</span>
            </h2>
            <div className="w-24 h-1.5 bg-blue-600 mx-auto mb-8"></div>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
              We provide complete reconditioning, repair, and supply of all major types of hydraulic pumps, including piston, gear, and vane pumps.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto px-4">
            {/* Why Choose Us - Left Side */}
            <div className="bg-white p-8 rounded-2xl shadow-lg border border-gray-100 hover:shadow-xl transition-shadow duration-300 h-full">
              <div className="bg-blue-50 w-14 h-14 rounded-xl flex items-center justify-center mb-6">
                <Zap className="text-blue-600" size={28} />
              </div>
              <h3 className="text-2xl font-semibold mb-6 text-[#00325b]">Why Choose Us</h3>
              <ul className="space-y-4">
                {keyPoints.map((point, index) => (
                  <li key={index} className="flex items-start group">
                    <div className="bg-green-100 p-1.5 rounded-full mr-3 mt-0.5 group-hover:bg-green-200 transition-colors">
                      <CheckCircle className="text-green-600" size={18} />
                    </div>
                    <span className="text-gray-700 group-hover:text-gray-900 transition-colors">{point}</span>
                  </li>
                ))}
              </ul>
            </div>
            
            {/* Our Objectives - Right Side */}
            <div className="bg-white p-8 rounded-2xl shadow-lg border border-gray-100 hover:shadow-xl transition-shadow duration-300 h-full">
              <div className="bg-blue-50 w-14 h-14 rounded-xl flex items-center justify-center mb-6">
                <Settings className="text-blue-600" size={28} />
              </div>
              <h3 className="text-2xl font-semibold mb-6 text-[#00325b]">Our Objectives</h3>
              <ul className="space-y-4">
                {objectives.map((objective, index) => (
                  <li key={index} className="flex items-start group">
                    <div className="bg-green-100 p-1.5 rounded-full mr-3 mt-0.5 group-hover:bg-green-200 transition-colors">
                      <CheckCircle className="text-green-600" size={18} />
                    </div>
                    <span className="text-gray-700 group-hover:text-gray-900 transition-colors">{objective}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Process Section */}
      {/* <motion.section 
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ 
          opacity: 1, 
          y: 0,
          transition: { duration: 0.6, ease: 'easeOut' }
        }}
        viewport={{ once: true, amount: 0.2 }}
        className="py-16 bg-white"
      >
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="text-center mb-12 px-4">
            <h2 className="text-3xl font-bold text-[#00325b] mb-4">Our Reconditioning Process</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              We follow a meticulous process to ensure every pump meets the highest standards of quality and performance.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 px-4">
            {processSteps.map((step, index) => (
              <motion.div 
                key={index}
                variants={item}
                className="bg-gray-50 p-6 rounded-lg shadow-sm hover:shadow-md transition-all duration-300 border border-gray-100"
              >
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                  <span className="text-blue-600 font-bold text-xl">{index + 1}</span>
                </div>
                <motion.h3 
                  variants={fadeInUp}
                  className="text-xl font-semibold mb-2 text-gray-900"
                >
                  {step.title}
                </motion.h3>
                <motion.p 
                  variants={fadeInUp}
                  className="text-gray-600"
                >
                  {step.description}
                </motion.p>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section> */}

      {/* CTA Section */}
      <motion.section 
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ 
          opacity: 1, 
          y: 0,
          transition: { duration: 0.6, ease: 'easeOut' }
        }}
        viewport={{ once: true, amount: 0.2 }}
        id="contact" 
        className="bg-blue-700 text-white py-16"
      >
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">Need Hydraulic Pump Services?</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Contact us today for expert hydraulic pump reconditioning and supply solutions.
          </p>
          <button 
            onClick={() => navigate('/contact')}
            className="bg-white text-blue-700 hover:bg-gray-100 font-medium py-3 px-8 rounded-lg transition-colors duration-300"
          >
            Get a Free Quote
          </button>
        </div>
      </motion.section>

      <Footer />
    </div>
  );
};

export default HydraulicPumpReconditioning;
